import { AddItemRequest } from "@/routes/basket/types/apiTypes/addItemRequest";
import { BasketDto } from "@/routes/basket/types/basketDto";
import { apiEndpoints } from "../api/api";
import { RemoveItemRequest } from "@/routes/basket/types/apiTypes/removeItemRequest";
import { ApplyDiscountCodeRequest } from "@/routes/basket/types/apiTypes/applyDiscountCodeRequest";
import { ApplyDiscountCodeToBasketItemRequest } from "@/routes/basket/types/apiTypes/applyDiscountCodeToBasketItemRequest";
import { RemoveDiscountCodeFromBasketItemRequest } from "@/routes/basket/types/apiTypes/removeDiscountCodeFromBasketItemRequest";
import { RemoveDiscountCodeRequest } from "@/routes/basket/types/apiTypes/removeDiscountCodeRequest";
import { CreateFreeOrderRequest } from "@/routes/basket/types/apiTypes/createFreeOrderRequest.ts";

class BasketService {
  private subscribers: Set<() => void>;
  private basketId: string | null = null;
  private basketItemCount: number = 0;
  //private basket: BasketDto | null;

  constructor() {
    this.basketId = localStorage.getItem("basketId");
    this.subscribers = new Set();
  }

  public async getBasket() {
    const response = await apiEndpoints.getBasket(this.basketId);
    if (response.success) {
      if (!response.data) {
        await this.removeAll();
        return null;
      }
      const basket = response.data as BasketDto;
      this.basketId = basket.id;
      this.basketItemCount = basket.items.length;
      localStorage.setItem("basketId", this.basketId);
      this.notifySubscribers();
      return basket;
    } else {
      throw new Error("Failed to add item to basket");
    }
  }

  public async addItemToBasket(input: AddItemRequest) {
    if (!input.basketId) input.basketId = this.basketId;
    const response = await apiEndpoints.addItemToBasket(input);
    if (response.success) {
      const basket = response.data as BasketDto;
      this.basketId = basket.id;
      this.basketItemCount = basket.items.length;
      localStorage.setItem("basketId", this.basketId);
      this.notifySubscribers();
    } else {
      throw new Error("Failed to add item to basket");
    }
  }

  public async removeItemFromBasket(input: RemoveItemRequest) {
    const response = await apiEndpoints.removeItemFromBasket(input);
    if (response.success) {
      const basket = response.data as BasketDto | null;
      if (!basket) {
        this.basketId = null;
        localStorage.removeItem("basketId");
        this.basketItemCount = 0;
        this.notifySubscribers();
        return null;
      } else {
        this.basketId = basket.id;
        localStorage.setItem("basketId", this.basketId);
        this.basketItemCount = basket.items.length;
        this.notifySubscribers();
        return basket;
      }
    } else {
      throw new Error("Failed to add item to basket");
    }
  }

  public async applyDiscountCodeToBasket(input: ApplyDiscountCodeRequest) {
    const response = await apiEndpoints.applyDiscountCodeToBasket(input);
    if (response.success) {
      const basket = response.data as BasketDto;
      this.basketId = basket.id;
      localStorage.setItem("basketId", this.basketId);
      return basket;
    } else {
      throw new Error("Failed to add item to basket");
    }
  }

  public async applyDiscountCodeToBasketItemBasket(
    input: ApplyDiscountCodeToBasketItemRequest
  ) {
    const response = await apiEndpoints.applyDiscountCodeToBasketItemBasket(
      input
    );
    if (response.success) {
      const basket = response.data as BasketDto;
      this.basketId = basket.id;
      localStorage.setItem("basketId", this.basketId);
      this.notifySubscribers();
      return basket;
    } else {
      throw new Error("Failed to add item to basket");
    }
  }

  public async removeDiscountCodeFromBasket(input: RemoveDiscountCodeRequest) {
    const response = await apiEndpoints.removeDiscountCodeFromBasket(input);
    if (response.success) {
      const basket = response.data as BasketDto;
      this.basketId = basket.id;
      localStorage.setItem("basketId", this.basketId);
      this.notifySubscribers();
      return basket;
    } else {
      throw new Error("Failed to add item to basket");
    }
  }

  public async removeDiscountCodeFromBasketItemBasket(
    input: RemoveDiscountCodeFromBasketItemRequest
  ) {
    const response = await apiEndpoints.removeDiscountCodeFromBasketItemBasket(
      input
    );
    if (response.success) {
      const basket = response.data as BasketDto;
      this.basketId = basket.id;
      localStorage.setItem("basketId", this.basketId);
      this.notifySubscribers();
      return basket;
    } else {
      throw new Error("Failed to add item to basket");
    }
  }

  public async createFreeOrder(basketId: string | null) {
    basketId = basketId ?? this.basketId;
    const response = await apiEndpoints.createFreeOrder({
      basketId: basketId,
    } as CreateFreeOrderRequest);
    if (response.success) {
      await this.removeAll();
      this.notifySubscribers();
      return response.data.id;
    } else {
      throw new Error("Failed to create order");
    }
  }

  public async removeAll() {
    localStorage.removeItem("basketId");
    this.basketId = null;
    this.basketItemCount = 0;
  }
  // ==========================================
  // SUBSCRIBE METHODS
  // ==========================================

  subscribe(callback: (basketCount: number) => void) {
    this.subscribers.add(callback as () => void);

    callback(this.basketItemCount ?? 0);

    return () => {
      this.subscribers.delete(callback as () => void);
    };
  }

  notifySubscribers() {
    this.subscribers.forEach((subscriber) => {
      (subscriber as (basket: number) => void)(this.basketItemCount ?? 0);
    });
  }
}
export const basketService = new BasketService();
